import React from "react";
import { Link } from "react-router-dom";
import styles from "../SubjectSinglePage.module.css";
import type {
  FeaturedSlot,
  GistItem,
  GistReaderMode,
  SubjectItem,
} from "../model/types";
import {
  countGistMetrics,
  formatDate,
  getAuthorName,
  getFirstMeaningfulText,
  getFullReaderText,
  makeExcerpt,
  makeReaderParagraphs,
} from "../model/subjectSingleModel";

type SubjectOverviewPageProps = {
  selectedSubject: SubjectItem;
  featuredSlots: FeaturedSlot[];
  openedGist: GistItem | null;
  subjectHero: React.ReactNode;
  onOpenGistReader: (gist: GistItem, mode?: GistReaderMode) => void;
  onCreateLinkedDraft: (gist: GistItem) => void;
  getSubjectUrl: (subject: SubjectItem) => string;
  isRegisteredUser: boolean;
  directGistCount: number;
  branchGistCount: number;
  childSubjectCount: number;
};

export function SubjectOverviewPage({
  selectedSubject,
  featuredSlots,
  openedGist,
  subjectHero,
  onOpenGistReader,
  onCreateLinkedDraft,
  getSubjectUrl,
  isRegisteredUser,
  directGistCount,
  branchGistCount,
  childSubjectCount,
}: SubjectOverviewPageProps) {
  const hiddenDescendantGistCount = Math.max(0, branchGistCount - directGistCount);
  return (
    <>
      {subjectHero}

      <section
        className={styles.exploreSection}
        aria-labelledby="subject-single-explore-title"
      >
        <div className={styles.exploreSectionHeader}>
          <div>
            <h2 id="subject-single-explore-title">Explore this subject</h2>
            <p>
              Showing direct gists for this subject only.
              {hiddenDescendantGistCount > 0
                ? ` ${hiddenDescendantGistCount} additional gists live deeper in child subjects.`
                : ""}
            </p>
          </div>
          {childSubjectCount > 0 && (
            <Link to={getSubjectUrl(selectedSubject)} className={styles.secondaryPillLink}>
              Open child subjects
            </Link>
          )}
        </div>
        <div className={styles.featureGrid}>
          {featuredSlots.map((slot) => {
            const metrics = slot.gist ? countGistMetrics(slot.gist) : null;
            const isActive = Boolean(slot.gist && openedGist?.id === slot.gist.id);
            return (
              <button
                type="button"
                key={slot.key}
                className={`${styles.featureCard} ${isActive ? styles.featureCardActive : ""}`}
                disabled={!slot.gist}
                onClick={() => slot.gist && onOpenGistReader(slot.gist, "read")}
              >
                <header>
                  <span
                    className={`${styles.featureIcon} ${styles[`featureIcon${slot.icon[0].toUpperCase()}${slot.icon.slice(1)}`]}`}
                    aria-hidden="true"
                  />
                  <strong>{slot.title}</strong>
                  <span className={styles.chevron} aria-hidden="true">
                    ›
                  </span>
                </header>
                <small>{slot.label}</small>
                {slot.gist ? (
                  <>
                    <h3>{slot.gist.title}</h3>
                    <p>{makeExcerpt(getFirstMeaningfulText(slot.gist), 120)}</p>
                    <footer>
                      <span>{metrics?.keyPointCount || 0} key points</span>
                      <span>•</span>
                      <span>
                        {formatDate(
                          metrics?.latestActivity || slot.gist.createdAt,
                        )}
                      </span>
                    </footer>
                  </>
                ) : (
                  <p>No gist is available for this slot yet.</p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {openedGist ? (
        <article
          className={styles.readerPanel}
          aria-labelledby="subject-single-reader-title"
        >
          <div className={styles.readerBadge}>Main overview</div>
          <h2 id="subject-single-reader-title">{openedGist.title}</h2>
          <div className={styles.readerMeta}>
            <span>{countGistMetrics(openedGist).keyPointCount} key point</span>
            <span>•</span>
            <span>Gist in {selectedSubject.title}</span>
            <span>•</span>
            <span>
              {formatDate(
                countGistMetrics(openedGist).latestActivity ||
                  openedGist.createdAt,
              )}
            </span>
            <span>•</span>
            <span>By {getAuthorName(openedGist)}</span>
          </div>
          <div className={styles.readerBody}>
            {makeReaderParagraphs(getFullReaderText(openedGist), 5).map(
              (paragraph, index) => (
                <p key={`${openedGist.id}-${index}`}>{paragraph}</p>
              ),
            )}
          </div>
          <div className={styles.actionBar} aria-label="Reader actions">
            <button
              type="button"
              onClick={() => onCreateLinkedDraft(openedGist)}
              disabled={!isRegisteredUser}
              title={!isRegisteredUser ? "Login required" : "Create a private draft reply and link it under this gist"}
            >
              🔗 Linked draft
            </button>
            <Link to={`/gist-lab?subjectId=${selectedSubject.id}&parentId=${openedGist.id}`}>
              ✎ Reply with new gist
            </Link>
            <button
              type="button"
              onClick={() => onOpenGistReader(openedGist, "tools")}
            >
              ▢ Comment on this gist
            </button>
            <button
              type="button"
              onClick={() => onOpenGistReader(openedGist, "advanced")}
            >
              ⌘ Open advanced view
            </button>
            <Link to={getSubjectUrl(selectedSubject)}>∞ See related threads</Link>
          </div>
        </article>
      ) : (
        <section className={styles.emptyReaderPanel}>
          <h2>No direct gists here yet</h2>
          <p>
            This subject has {childSubjectCount} direct child subject{childSubjectCount === 1 ? "" : "s"}
            {hiddenDescendantGistCount > 0
              ? ` and ${hiddenDescendantGistCount} gists deeper in the branch.`
              : "."} Use child subjects for navigation, or create a direct gist here when this subject needs its own article.
          </p>
        </section>
      )}
    </>
  );
}
